import * as React from "react";
import { Input, type InputProps } from "./input.js";

export interface PhoneInputProps
  extends Omit<InputProps, "value" | "onChange" | "type"> {
  /** E.164 value, e.g. "+15551234567", or "" when empty. */
  value: string;
  /** Called with the cleaned E.164 string ("" when cleared). */
  onChange: (e164: string) => void;
}

const MAX_NATIONAL = 10;

/**
 * Strip everything but digits and drop a NANP country-code "1" — North
 * American area codes never start with 1, so a leading 1 is always the
 * country code, whether the user typed it or it came from a stored
 * "+1…" value.
 */
function nationalDigits(raw: string): string {
  let d = raw.replace(/\D/g, "");
  if (d.startsWith("1")) d = d.slice(1);
  return d.slice(0, MAX_NATIONAL);
}

/** Mask a raw/E.164 phone string to "+1 (555) 123-4567" for display. */
export function formatPhoneDisplay(raw: string): string {
  const national = nationalDigits(raw ?? "");
  if (!national) return "";
  const area = national.slice(0, 3);
  const prefix = national.slice(3, 6);
  const line = national.slice(6, 10);
  let out = `+1 (${area}`;
  if (area.length === 3) out += ")";
  if (prefix) out += ` ${prefix}`;
  if (line) out += `-${line}`;
  return out;
}

/** Reduce any typed phone text to a clean E.164 string ("" when empty). */
export function phoneInputToE164(raw: string): string {
  const national = nationalDigits(raw ?? "");
  return national ? `+1${national}` : "";
}

/**
 * Given the current E.164 value and the raw text the input now holds,
 * compute the next E.164 value.
 *
 * The mask inserts formatting characters — `(`, `)`, ` `, `-` — that
 * carry no digits. Backspacing one of those leaves the digits
 * unchanged, so a naive reformat would re-insert it and the caret would
 * appear stuck (e.g. you can never delete past the `)` in `+1 (310)`).
 * When we see that no-op deletion — same digits but a shorter string —
 * we drop the adjacent digit instead, so Backspace always makes
 * progress through the number.
 */
export function nextPhoneValue(current: string, nextRaw: string): string {
  const currentDigits = nationalDigits(current ?? "");
  const nextDigits = nationalDigits(nextRaw);
  const deletedOnlyFormatting =
    nextDigits === currentDigits &&
    nextRaw.length < formatPhoneDisplay(current ?? "").length;
  if (deletedOnlyFormatting) {
    return phoneInputToE164(currentDigits.slice(0, -1));
  }
  return phoneInputToE164(nextRaw);
}

/**
 * Phone field that masks to the North American `+1 (555) 123-4567`
 * shape and emits a clean E.164 string. Fully controlled by the E.164
 * `value` — the display is derived on every render, so there's no
 * internal state to drift out of sync.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, placeholder, ...props }, ref) => {
    return (
      <Input
        {...props}
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={formatPhoneDisplay(value ?? "")}
        placeholder={placeholder ?? "+1 (555) 123-4567"}
        onChange={(e) => onChange(nextPhoneValue(value ?? "", e.target.value))}
      />
    );
  },
);
PhoneInput.displayName = "PhoneInput";
